
import { Component, Mixins, Emit } from 'vue-property-decorator'
import { handlerClick } from './notify_handler'
import PaginationMixin from '@/mixins/pagination-mixin'
import { useNotifyStore } from '@/store/notify'
import { NotifyListOutput } from '@/api/swagger'

interface Type {
  data?: unknown | null
  disabled: boolean
  label: string
  value: string
  sortId: number
}

const notifyStore = useNotifyStore()

@Component({})
export default class extends Mixins(PaginationMixin) {
  private notifyStore = notifyStore

  private visible = false
  private openKeys: string[] = ['notify']
  private selectedKeys: string[] = ['all']

  private loading = false
  private processing = false

  private selectedRowKeys: string[] = [] // 当前也选择的行

  private dataSource: NotifyListOutput[] = []

  private types: Type[] = []

  private expandedRowKeys: string[] = []

  private query: {
    readed?: boolean
    type?: string
  } = {
    readed: undefined, // 是否已读
    type: undefined, // 类型
  }

  private onCancel() {
    this.visible = false
  }

  private get columns() {
    return [
      {
        title: '标题',
        dataIndex: 'title',
        scopedSlots: { customRender: 'record_title' },
      },
      {
        title: '类型',
        dataIndex: 'typeLabel',
        scopedSlots: { customRender: 'typeLabel' },
        width: 120,
        align: 'center',
      },
      {
        title: '时间',
        dataIndex: 'creationTime',
        scopedSlots: { customRender: 'creationTime' },
        width: 240,
        align: 'center',
      },
    ]
  }

  private get rowSelection() {
    return {
      onChange: (selectedRowKeys) => {
        this.selectedRowKeys = selectedRowKeys
      },
      getCheckboxProps: (record) => ({
        props: {
          disabled: false,
          name: record.name,
        },
      }),
    }
  }

  private extendRow(row: NotifyListOutput) {
    const index = this.expandedRowKeys.findIndex((v) => v === row.id)

    if (index >= 0) {
      this.expandedRowKeys.splice(index, 1)
    } else {
      this.expandedRowKeys.push(row.id || '')
    }
  }

  private switchMenu(menu: string, readed?: boolean) {
    this.selectedKeys = [menu]
    this.query.readed = readed
    this.pagination.current = this.pagination.defaults.current
    this.selectedRowKeys = []
    this.load()
  }

  private changeType(type?: string) {
    this.query.type = type
    this.pagination.current = this.pagination.defaults.current
    this.selectedRowKeys = []
    this.load()
  }

  @Emit('change')
  private onChange() {
    // 通知上层
  }

  // 标记为已读
  private async markAsRead() {
    this.processing = true
    try {
      const selectedRowKeys = [...this.selectedRowKeys]
      await this.$http.post('/api/Notify/Readed', {
        ids: selectedRowKeys,
      })
      this.$message.success('标记成功')
      for (const row of this.dataSource) {
        if (selectedRowKeys.includes(row.id || '')) {
          row.readed = true
        }
      }
      this.onChange()
    } catch (err) {
      this.$message.error(err.message)
    } finally {
      this.processing = false
    }
  }

  // 标记所有为已读
  private async markAllAsRead() {
    this.processing = true
    try {
      await this.$http.post('/api/Notify/ReadAll')
      this.$message.success('标记成功')
      for (const row of this.dataSource) {
        row.readed = true
      }
      this.onChange()
    } catch (err) {
      this.$message.error(err.message)
    } finally {
      this.processing = false
    }
  }

  // 删除消息
  private async remove() {
    this.processing = true
    try {
      const selectedRowKeys = [...this.selectedRowKeys]
      await this.$http.post('/api/Notify/Remove', {
        ids: selectedRowKeys.join(','),
      })
      this.$message.success('删除成功')
      this.onChange()
      await this.load()
    } catch (err) {
      this.$message.error(err.message)
    } finally {
      this.processing = false
    }
  }

  // 删除所有消息
  private async removeAll() {
    this.processing = true
    try {
      await this.$http.post('/api/Notify/RemoveAll')
      this.$message.success('删除成功')
      this.onChange()
      this.reset()
      await this.load()
    } catch (err) {
      this.$message.error(err.message)
    } finally {
      this.processing = false
    }
  }

  private async handle(notify) {
    if (notify.type == 'test_program/sign_in') {
      this.$router.push(
        `/testing-management/programme/view?engineeringId=${notify.payload?.engineeringId}&programmeId=${notify.payload.programmeId}&type=2&isOld=${notify.payload.isOld}&isInform=1`,
      )
      this.onCancel()
    }
    if (notify.type == 'test/report_upload_sign') {
      this.$router.push({
        name: '/test-report/list',
        query: { testingCode: notify.payload.testingCode },
      })
      this.onCancel()
    }
    await this.$http.post('/api/Notify/Readed', {
      ids: [notify.id],
    })
    notify.readed = true
    this.onChange()
    const { closeDialog, redirect } = await handlerClick(notify)

    if (closeDialog) {
      this.hide()
    }

    if (redirect) {
      await this.$router.push(redirect)
    }
  }

  // 重置所有
  public reset() {
    this.pagination.reset()
    this.selectedKeys = ['all']
    this.query.type = undefined
    this.query.readed = undefined
    this.selectedRowKeys = []
  }

  private onPaginationChange(page: number, pageSize: number) {
    this.pagination.current = page
    this.pagination.pageSize = pageSize
    this.load()
  }

  private onPageSizeChange(current: number, pageSize: number) {
    this.pagination.current = this.pagination.defaults.current
    this.pagination.pageSize = pageSize
    this.load()
  }

  private async getTypes() {
    const res: any = await this.$http.get('/api/Notify/Options')
    this.types = [{ label: '全部', value: '', disabled: false, sortId: 0 } as Type].concat(res.data)
  }

  public async load() {
    this.notifyStore.updateUnreadCount()
    this.loading = true
    try {
      const res = await this.$http.get('/api/Notify/Page', {
        Page: this.pagination.JSON.current,
        PageSize: this.pagination.JSON.pageSize,
      })

      this.dataSource = res.data.list
      this.pagination.update(res.data)
    } finally {
      this.loading = false
    }
  }

  public open() {
    this.visible = true
    this.reset()
    this.load()
    this.onChange()
  }

  public hide() {
    this.visible = false
    this.reset()
  }

  public mounted() {
    if (location.pathname.startsWith('/fire')) {
      this.getTypes()
    }
  }
}
